import React from 'react';
import { Divider, notification, Spin } from 'antd';

import { Style, Stroke, Fill } from 'ol/style';
import Feature from 'ol/Feature';
import { Vector as VectorLayer } from 'ol/layer';
import VectorSource from 'ol/source/Vector';
import CircleStyle from 'ol/style/Circle';
import { Point } from 'ol/geom';
import * as olProj from 'ol/proj';

import { onDeleteLayers } from '@project/utils/deleteLayers';
import { Route } from '../Body';
import { useStore } from '@project/components/hooks';
import { ZINDEX } from '@project/components/Map/constants';

import { Sign98Icon } from '@assets/icons/route/Sign/-98';
import { SignMin8Icon } from '@assets/icons/route/Sign/-8';
import { SignMin7Icon } from '@assets/icons/route/Sign/-7';
import { SignMin3Icon } from '@assets/icons/route/Sign/-3';
import { SignMin2Icon } from '@assets/icons/route/Sign/-2';
import { SignMin1Icon } from '@assets/icons/route/Sign/-1';
import { Sign0Icon } from '@assets/icons/route/Sign/0';
import { Sign1Icon } from '@assets/icons/route/Sign/1';
import { Sign2Icon } from '@assets/icons/route/Sign/2';
import { Sign3Icon } from '@assets/icons/route/Sign/3';
import { Sign4Icon } from '@assets/icons/route/Sign/4';
import { Sign5Icon } from '@assets/icons/route/Sign/5';
import { Sign6Icon } from '@assets/icons/route/Sign/6';
import { Sign7Icon } from '@assets/icons/route/Sign/7';
import { Sign8Icon } from '@assets/icons/route/Sign/8';

import '@assets/scss/index.scss';
import '../style.scss';

type contentType = {
  distance: number;
  sign: number;
  interval: number[];
  text: string;
  time: number;
  street_name: string;
};

export interface ContentView {
  route: Route;
  isLoading?: boolean;
}

export const ContentRoute: React.FC<ContentView> = ({ isLoading, route }) => {
  const { map } = useStore();

  const parserIcon = (key: number) => {
    switch (key) {
      case -98:
        // без знания, правый или левый поворот
        return <Sign98Icon />;
      case -8:
        // левый разворот
        return <SignMin8Icon />;
      case -7:
        // держитесь левее
        return <SignMin7Icon />;
      case -3:
        // круто поверните налево
        return <SignMin3Icon />;
      case -2:
        // поверните налево
        return <SignMin2Icon />;
      case -1:
        // поворот немного налево
        return <SignMin1Icon />;
      case 0:
        // продолжите движение по улице
        return <Sign0Icon />;
      case 1:
        // поворот немного направо
        return <Sign1Icon />;
      case 2:
        // поворот направо
        return <Sign2Icon />;
      case 3:
        // круто повернуть направо
        return <Sign3Icon />;
      case 4:
        // финишная инструкция перед последней точкой
        return <Sign4Icon />;
      case 5:
        // инструкция перед промежуточным пунктом
        return <Sign5Icon />;
      case 6:
        // инструкция перед выездом на кольцевую развязку
        return <Sign6Icon />;
      case 7:
        // держись правее
        return <Sign7Icon />;
      case 8:
        // правый разворот
        return <Sign8Icon />;
      default:
        return;
    }
  };

  // duration приходит в миллисекундах
  const parserTime = (duration: number) => {
    const timeArray: string[] = [];

    const msInHour = 1000 * 60 * 60;
    const hours = Math.trunc(duration / msInHour);
    if (hours > 0) {
      timeArray.push(hours + 'ч');
      duration = duration - hours * msInHour;
    }

    const msInMinute = 1000 * 60;
    const minutes = Math.trunc(duration / msInMinute);
    if (minutes > 0) {
      timeArray.push(minutes + 'мин');
      duration = duration - minutes * msInMinute;
    }

    const seconds = Math.trunc(duration / 1000);
    if (seconds > 0) {
      timeArray.push(seconds + 'с');
    }

    return timeArray.join(' ');
  };

  const parserDistance = (distance: number) => {
    const value = Math.round(distance);
    if (String(value).length >= 4) {
      return value / 1000 + 'км';
    } else {
      if (value <= 0) return;

      return value + 'м';
    }
  };

  const atCoordinateBy = (interval: number[]) => {
    const olMap = map.mapProperties.map;
    const key = interval[0];
    let coords;
    if (key || key === 0) coords = route.points[key];
    if (!coords) return;
    coords = olProj.transform(
      coords,
      'EPSG:4326',
      olMap.getView().getProjection().getCode(),
    );
    onDeleteLayers('_geoMarker');

    const geoMarker = new Feature({
      type: 'geoMarker',
      geometry: new Point(coords),
    });

    const vector = new VectorLayer({
      source: new VectorSource({ features: [geoMarker] }),
      style: new Style({
        image: new CircleStyle({
          radius: 7,
          fill: new Fill({ color: 'orange' }),
          stroke: new Stroke({
            color: 'white',
            width: 2,
          }),
        }),
      }),
    });
    vector.set('_geoMarker', true);
    vector.setZIndex(ZINDEX.ROUTE_POINT);

    const view = olMap.getView();
    const size = olMap.getSize();

    olMap.addLayer(vector);

    if (!size || size[0] === undefined || size[1] === undefined) {
      notification.error({ message: 'Не удалось определить точку на карте' });

      return;
    }

    view.centerOn(coords, size, [size[0] / 2 + 100, size[1] / 2]);
  };

  const routeHeaderView = route.distance !== 0 && route.time !== 0 && (
    <>
      <div className="content-header">
        <div>
          <b>{parserTime(route.time)}</b>
        </div>
        <div>
          <span>{parserDistance(route.distance)}</span>
        </div>
      </div>
      <Divider className="divider" />
    </>
  );

  return (
    <div className="route-content">
      {isLoading && (
        <div className="spin-route" style={{ bottom: '10%' }}>
          <Spin size="large" />
        </div>
      )}
      {routeHeaderView}
      <div className="route-body">
        {route.instructions.map((item: contentType, i: number) => {
          return (
            <div
              key={i}
              className="content-body"
              onClick={() => atCoordinateBy(item.interval)}
            >
              <div className="content-icon">{parserIcon(item.sign)}</div>
              <div className="content-body-title">{item.text}</div>
              <div className="content-time">
                <div>{parserDistance(item.distance)}</div>
                <div>{parserTime(item.time)}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
